import { IFormDefinition } from '@introspection/index';
import { Component, DefineDependencies } from '@rhtml/component';
import { Container } from '@rxdi/core';
import { AbstractInput } from '@rxdi/forms';
import { html, LitElement, property, unsafeCSS } from '@rxdi/lit-html';
import { ModalViewService } from '@shared/services';
import { SteelFabTheme } from '@shared/styles/theme';
import * as DynamicFormStyles from 'bundle-text:./dynamic-form.css';
import { delay, of } from 'rxjs';

/**
 * @customElement dynamic-form-modal
 */
@Component({
  Settings: {
    selector: 'dynamic-form-modal',
    styles: [SteelFabTheme, unsafeCSS(DynamicFormStyles)],
  },
  Loading: () => html`
    <div style="text-align: center; padding: 118px;">
      <sl-spinner color="black" height="100" width="100" stroke="0.2"></sl-spinner>
    </div>
  `,
  Providers: DefineDependencies(ModalViewService)(Container),
  State: function (this: DynamicFormModal<never>) {
    return of({ model: this.model, form: this.form }).pipe(delay(this.isMobile() ? 200 : 0));
  },
  Render: ([modal]) =>
    function (this, state) {
      const { model, form } = state;
      return html`
        <dynamic-form
          .definition=${form as never}
          .model=${model}
          .options=${this.options}
          .layout=${this.isMobile() ? false : this.layout}
          .context=${this.context}
          @change=${({ detail }) => this.dispatchEvent(new CustomEvent('change', { detail }))}
          .errorTemplate=${(input: AbstractInput) => {
            if (
              input.tagName === 'JSON-INPUT' ||
              input.tagName === 'BASH-EDITOR' ||
              input.tagName === 'CODE-EDITOR' ||
              input.tagName === 'R-DROPDOWN'
            ) {
              return;
            }
            if (input && input.touched && !input.validity.valid) {
              const message = input.validationMessage;
              input.classList.add('is-invalid');

              return html`
                <style>
                  .validation-error {
                    color: #a94442;
                    font-size: 12px;
                    padding-top: 5px;
                  }
                </style>
                <span class="validation-error" part="error-message">${message}</span>
              `;
            }
            input && input?.classList?.remove('is-invalid');
          }}
          @submit=${(e: CustomEvent<never>) => {
            modal.close(e.detail);
          }}
        >
          <div slot="header" style="padding: 20px 10px 20px 30px;color: white">
            <h2 style="margin:0">
              <slot name="header">Generic Header</slot>
            </h2>
          </div>
          <div slot="content-footer">
            <slot name="content"></slot>
          </div>
          <div slot="submit">
            <button .reversed=${true}>
              <slot name="buttons">Generic Button</slot>
            </button>
          </div>
        </dynamic-form>
      `;
    },
})
export class DynamicFormModal<T> extends LitElement {
  @property({ type: Object })
  model: T;

  @property({ type: Object })
  options: Record<string, never[]> = {};

  @property({ type: Object })
  form: IFormDefinition;

  @property({ type: String })
  projectId: string;

  @property({ type: Boolean })
  layout = false;

  @property({ type: Object })
  context: Record<string, string> = { mode: 'create' };

  private isMobile() {
    return window.matchMedia('(width <= 900px)').matches;
  }
}
